/**
 * MQTT vztahy a odběry — familiar name + poslední kontakt, ne jen UUID.
 */
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Clock, Radio, Wifi } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { StatusDot } from '../../../components/ui/StatusDot';
import { useMeshPeers } from '../../../widgets/NodePicker';
import { peerMatchesKey, resolveNodeDisplayLabel, type MeshPeer } from '../../../widgets/node-targets';
import {
  classifyClients,
  clientContactIso,
  clientFamiliarName,
  clientMatchesPeerKeys,
  connectorFamiliarName,
  connectorIdentityKeys,
  contactAgeSeconds,
  groupSubsByTopic,
  mqttClientId,
  mqttKind,
  peerRelationship,
  peerMqttStatus,
  parseBridgeClient,
  shortNode,
  subsForClient,
  type LabelOf,
  type MqttBridgeRow,
  type MqttClientRow,
  type MqttSubRow,
} from '../../../lib/mqtt-view';

function KindBadge({ clientid, isBridge }: { clientid: string; isBridge?: boolean }) {
  const k = mqttKind(clientid, isBridge);
  const label = k === 'bridge' ? 'most' : k === 'mcp' ? 'MCP' : k === 'fleet' ? 'fleet' : k === 'api' ? 'API' : 'klient';
  const variant = k === 'bridge' ? 'purple' : k === 'mcp' ? 'secondary' : 'gray';
  return <Badge variant={variant} className='text-[10px]'>{label}</Badge>;
}

function peerKeyList(peer: MeshPeer): string[] {
  return [peer.stable_id, peer.name, peer.label, peer.route_name, peer.host]
    .map((v) => (v ?? '').trim())
    .filter(Boolean);
}

function useMqttLabels() {
  const { data: peers = [] } = useMeshPeers({ includeLocal: true });
  const labelOf: LabelOf = useCallback((key: string) => {
    const peer = peers.find((p) => peerMatchesKey(p, key));
    return peer ? resolveNodeDisplayLabel(key, peers, '') : '';
  }, [peers]);
  return { peers, labelOf };
}

function ContactAge({ iso, connected }: { iso?: string | null; connected?: boolean }) {
  const { t } = useTranslation();
  if (connected) return <span className='text-green-400' title={iso || undefined}>{t('mqtt.connectedNow')}</span>;
  const sec = contactAgeSeconds(iso);
  if (sec == null) {
    return <span className='text-gray-600'>—</span>;
  }
  let text: string;
  if (sec < 15) text = t('mqtt.contactNow', 'teď');
  else if (sec < 60) text = t('mqtt.contactSeconds', '{{n}}s', { n: sec });
  else if (sec < 3600) text = t('mqtt.contactMinutes', '{{n}} min', { n: Math.floor(sec / 60) });
  else if (sec < 86400) text = t('mqtt.contactHours', '{{n}} h', { n: Math.floor(sec / 3600) });
  else text = t('mqtt.contactDays', '{{n}} d', { n: Math.floor(sec / 86400) });
  return (
    <span title={iso || undefined} className='text-gray-400'>
      <Clock size={11} className='inline mr-1 -mt-px' />
      {text}

    </span>
  );
}

function MeshNodesStrip({
  peers,
  clients,
}: {
  peers: MeshPeer[];
  clients: MqttClientRow[];
}) {
  const { t } = useTranslation();
  if (peers.length === 0) return null;
  const rows = [...peers].sort((a, b) => {
    if (a.is_local !== b.is_local) return a.is_local ? -1 : 1;
    return resolveNodeDisplayLabel(a.stable_id || a.name, peers).localeCompare(
      resolveNodeDisplayLabel(b.stable_id || b.name, peers),
    );
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Wifi} className='text-sm'>
          {t('mqtt.meshNodes', 'Uzly')}
          <span className='ml-2 text-[11px] font-normal text-gray-500'>{rows.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className='divide-y divide-gray-800/80'>
        {rows.map((peer) => {
          const keys = peerKeyList(peer);
          const matching = clients.filter((c) => clientMatchesPeerKeys(c, keys));
          const client = matching.find((c) => c.connected) || matching[0];
          const iso = clientContactIso(client);
          const relationship = peerRelationship(peer);
          const name = resolveNodeDisplayLabel(peer.stable_id || peer.name, peers);
          return (
            <div key={peer.stable_id || peer.name} className='flex items-center gap-3 py-2 first:pt-0 last:pb-0'>
              <StatusDot
                status={peerMqttStatus(peer, matching)}
              />
              <div className='min-w-0 flex-1'>
                <div className='flex items-center gap-2 min-w-0'>
                  <span className='text-sm font-medium text-white truncate'>{name}</span>
                  {peer.is_local && (
                    <span className='text-[10px] text-blue-400'>{t('mqtt.localNode', 'místní')}</span>
                  )}
                  <Badge variant='secondary' className='text-[10px]'>{t(`mqtt.relationship.${relationship}`)}</Badge>
                </div>
                <div className='text-[11px] text-gray-500 truncate'>
                  {peer.host || shortNode(peer.stable_id || peer.name)}
                  {client?.ip_address ? ` · ${client.ip_address}` : ''}
                </div>
              </div>
              <div className='shrink-0 text-right text-[11px]'>
                <ContactAge iso={iso} connected={client?.connected} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function StationLocation({ station }: { station: MqttClientRow }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['mqtt-station-location', station.stable_id, station.ip_address],
    staleTime: 60_000, retry: false,
    queryFn: async () => {
      type Position = { lat: number; lon: number; source: string; recorded_at: string };
      const r = await api.mcp<{ ok: boolean; positions?: Position[] }>('geo.position_list', {
        kind: 'station', entity_id: station.stable_id, limit: 1,
      });
      if (r.ok && r.positions?.[0]) return r.positions[0];
      if (!station.ip_address) return null;
      const ip = await api.mcp<{ ok: boolean; positions?: Position[] }>('geo.position_list', {
        kind: 'caller', entity_id: station.ip_address, limit: 1,
      });
      return ip.ok ? ip.positions?.[0] ?? null : null;
    },
  });
  return <div className='text-xs text-gray-400'>
    {data ? `${data.lat.toFixed(5)}, ${data.lon.toFixed(5)} · ${data.source} · ${data.recorded_at}` : t('mqtt.locationUnavailable')}
  </div>;
}

function StationContacts({ stations }: { stations: MqttClientRow[] }) {
  const { t } = useTranslation();
  if (!stations.length) return null;
  return <Card><CardHeader><CardTitle>ACM-LINK</CardTitle></CardHeader>
    <CardContent className='space-y-3'>
      <p className='text-xs text-gray-400'>{t('mqtt.intermittentHint')}</p>
      {stations.map((s) => <div key={s.username} className='space-y-1'>
        <div className='flex items-center gap-2'>
          <StatusDot status={s.connected ? 'running' : 'offline'} />
          <span>{s.label || s.stable_id}</span>
          <Badge variant='secondary'>ACM-LINK</Badge>
          <span className='ml-auto text-xs'>{t(s.connected ? 'mqtt.connectedNow' : 'mqtt.offlineExpected')}</span>
        </div>
        <div className='text-xs text-gray-400'>{t('mqtt.lastContact')}: {s.last_contact || '—'} · IP: {s.ip_address || '—'}</div>
        <StationLocation station={s} />
      </div>)}
    </CardContent></Card>;
}

export function MqttRelations({
  clients,
  subs,
  bridges,
}: {
  clients: MqttClientRow[];
  subs: MqttSubRow[];
  bridges: MqttBridgeRow[];
}) {
  const { t } = useTranslation();
  const { peers, labelOf } = useMqttLabels();
  const stations = [...new Map(clients.filter((c) => c.relationship === 'acm_link')
    .sort((a,b) => Number(a.connected) - Number(b.connected))
    .map((c) => [c.username, c])).values()];
  const { bridgesIn, local } = classifyClients(clients.filter((c) => c.relationship !== 'acm_link'));
  const dryIngress = bridges.filter((b) => b.diagnostic_status === 'error');

  return (
    <div className='space-y-4'>
      {dryIngress.length > 0 && (
        <div className='rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200'>
          <AlertTriangle size={13} className='inline mr-1.5 -mt-0.5' />
          {t(
            'mqtt.bridgeDiagnosticFailures',
            '{{n}} odchozích mostů je připojených, ale bez běžícího ingressu — z protějšku sem nic neteče.',
            { n: dryIngress.length },
          )}
        </div>
      )}

      <MeshNodesStrip peers={peers} clients={clients} />
      <StationContacts stations={stations} />

      <div className='grid gap-4 lg:grid-cols-2'>
        <Card>
          <CardHeader>
            <CardTitle icon={Wifi} className='text-sm'>
              {t('mqtt.outgoingBridges', 'Odchozí mosty')}
              <span className='ml-2 text-[11px] font-normal text-gray-500'>{bridges.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2'>
            {bridges.length === 0
              ? (
                <p className='text-xs text-gray-500'>
                  {t('mqtt.noOutgoingBridges', 'Žádný MQTT connector. Cluster peery se tady neukážou.')}
                </p>
              )
              : bridges.map((b) => {
                const name = connectorFamiliarName(b.name, labelOf);
                const peer = peers.find((p) =>
                  connectorIdentityKeys(b.name).some((k) => peerMatchesKey(p, k))
                );
                return (
                  <div
                    key={b.name}
                    className='rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2'
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-sm font-medium text-white truncate'>{name}</span>
                      <Badge variant={b.diagnostic_status === 'error' ? 'error' : b.diagnostic_status === 'ok' ? 'green' : 'gray'} className='text-[10px]'>
                        {b.diagnostic_status === 'unknown' ? t('mqtt.verificationUnknown') : b.status}
                      </Badge>
                    </div>
                    <div className='mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500'>
                      <span>
                        {t(b.expected_topics?.length ? 'mqtt.ingressCount' : 'mqtt.egressCount', {
                          run: b.expected_topics?.length ? b.ingress_running ?? 0 : b.egress_running ?? 0,
                          total: b.expected_topics?.length || b.egress_total || 0,
                        })}
                        {(b.ingress_names ?? []).length > 0
                          ? ` · ${(b.ingress_names ?? []).join(', ')}`
                          : ''}
                      </span>
                      <ContactAge iso={peer?.last_seen} connected={b.connected} />
                    </div>
                    {name !== b.name && (
                      <div className='mt-0.5 font-mono text-[10px] text-gray-600 truncate'>{b.name}</div>
                    )}
                    {(b.missing_topics?.length ?? 0) > 0 && (
                      <p className='mt-1 text-[11px] text-amber-300'>
                        {t('mqtt.missingTopics')}: {b.missing_topics?.join(', ')}
                      </p>
                    )}
                  </div>
                );
              })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle icon={Radio} className='text-sm'>
              {t('mqtt.incomingBridges', 'Příchozí mosty')}
              <span className='ml-2 text-[11px] font-normal text-gray-500'>{bridgesIn.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2'>
            {bridgesIn.length === 0
              ? (
                <p className='text-xs text-gray-500'>
                  {t('mqtt.noIncomingBridges', 'Žádný bridge klient na tomto brokeru.')}
                </p>
              )
              : bridgesIn.map((c) => {
                const id = mqttClientId(c);
                const rel = parseBridgeClient(id);
                const from = rel ? (labelOf(rel.from) || clientFamiliarName(c, labelOf)) : clientFamiliarName(c, labelOf);
                const to = rel ? (labelOf(rel.to) || rel.to) : '';
                const topics = subsForClient(subs, id);
                return (
                  <div key={id} className='rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='text-sm font-medium text-white truncate'>
                        {rel
                          ? (
                            <span className='inline-flex items-center gap-1.5'>
                              <span>{from}</span>
                              <ArrowRight size={12} className='text-gray-600 shrink-0' />
                              <span>{to}</span>
                            </span>
                          )
                          : from}
                      </span>
                      <span className='ml-auto shrink-0 text-[11px]'>
                        <ContactAge iso={clientContactIso(c)} connected={c.connected} />
                      </span>
                    </div>
                    <div className='mt-1 text-[11px] text-gray-500'>
                      <Badge variant='secondary'>{t(`mqtt.relationship.${c.relationship || 'unknown'}`)}</Badge>{' '}
                      {c.ip_address || '—'}
                      {` · ${topics.length} ${t('mqtt.subsShort', 'odběrů')}`}
                    </div>
                    {!!c.missing_topics?.length && <p className='text-xs text-red-400'>{t('mqtt.missingTopics')}: {c.missing_topics.join(', ')}</p>}
                    <div className='mt-0.5 font-mono text-[10px] text-gray-600 truncate'>{id}</div>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle icon={Radio} className='text-sm'>
            {t('mqtt.localClients', 'Místní služby')}
            <span className='ml-2 text-[11px] font-normal text-gray-500'>{local.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          {local.length === 0
            ? <p className='text-xs text-gray-500'>{t('mqtt.noLocalClients', 'Nikdo místní není připojený.')}</p>
            : local.map((c) => {
              const id = mqttClientId(c);
              const topics = subsForClient(subs, id);
              const name = clientFamiliarName(c, labelOf);
              return (
                <div key={id} className='rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2'>
                  <div className='flex items-center gap-2 min-w-0'>
                    <KindBadge clientid={id} isBridge={c.is_bridge} />
                    <span className='text-sm font-medium text-white truncate'>{name}</span>
                    <span className='ml-auto text-[11px] shrink-0'>
                      <ContactAge iso={clientContactIso(c)} connected={c.connected} />
                    </span>
                  </div>
                  <div className='mt-0.5 flex items-center gap-2 text-[11px] text-gray-500'>
                    <code className='truncate text-gray-600'>{id}</code>
                    <span className='ml-auto shrink-0'>
                      {topics.length || c.subscriptions_cnt || 0} {t('mqtt.subsShort', 'odběrů')}
                    </span>
                  </div>
                  {topics.length > 0 && (
                    <ul className='mt-1 space-y-0.5'>
                      {topics.slice(0, 6).map((s) => (
                        <li key={s.topic} className='font-mono text-[10px] text-gray-500 truncate'>
                          {s.topic}
                        </li>
                      ))}
                      {topics.length > 6 && (
                        <li className='text-[10px] text-gray-600'>+{topics.length - 6}</li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}

export function MqttSubscriptions({
  subs,
  clients = [],
}: {
  subs: MqttSubRow[];
  clients?: MqttClientRow[];
}) {
  const { t } = useTranslation();
  const { labelOf } = useMqttLabels();
  const groups = groupSubsByTopic(subs);
  const families = [...new Set(groups.map((g) => g.family))];
  const nameById = new Map<string, string>();
  for (const c of clients) {
    nameById.set(mqttClientId(c), clientFamiliarName(c, labelOf));
  }
  const nameOf = (id: string) => nameById.get(id) || labelOf(id) || shortNode(id);
  if (groups.length === 0) {
    return (
      <EmptyState
        icon={<Radio size={24} />}
        title={t('mqtt.noSubsTitle', 'Žádné odběry')}
        description={t('mqtt.noSubsHint', 'Nikdo na tomto brokeru nic neposlouchá — nebo EMQX API neodpovědělo.')}
      />
    );
  }
  return (
    <div className='space-y-4'>
      {families.map((fam) => (
        <div key={fam}>
          <h4 className='text-[11px] uppercase tracking-wider text-gray-500 mb-2'>{fam}</h4>
          <div className='rounded-xl border border-gray-800 overflow-hidden'>
            {groups.filter((g) => g.family === fam).map((g) => (
              <div
                key={g.topic}
                className='px-3 py-2 border-t border-gray-800/80 first:border-t-0 flex items-start gap-3'
              >
                <div className='flex-1 min-w-0'>
                  <code className='text-[11px] text-gray-200 break-all'>{g.topic}</code>
                  <div className='mt-1 flex flex-wrap gap-1'>
                    {g.clients.slice(0, 6).map((id) => (
                      <span
                        key={id}
                        className='rounded bg-gray-900 px-1.5 py-0.5 text-[10px] text-gray-300'
                        title={id}
                      >
                        {nameOf(id)}
                      </span>
                    ))}
                    {g.clients.length > 6 && (
                      <span className='text-[10px] text-gray-600'>+{g.clients.length - 6}</span>
                    )}
                  </div>
                </div>
                <div className='shrink-0 text-right'>
                  <div className='text-xs text-gray-300'>{g.clients.length}</div>
                  <div className='text-[10px] text-gray-600'>qos {g.qos}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
